// @flow
/* eslint-disable no-restricted-globals */
import * as React from 'react';
import deepEqual from 'fast-deep-equal';
import match from './match';
import type { Route } from './types';
import routerContext from './RouterContext';

/** Convenience wrapper hook around the RouteConsumer API to manage state within the routes
 *
 * Note this will NOT appear in React DevTools until the following issue is fixed: https://github.com/facebook/react/pull/14940
 * */

type RouteState = {|
  /** Returns true if the path is a subset of the current route */
  matches: boolean,
  /** A new route that contains the union of the current route and the path */
  to: Route,
  /** A new route that contains the set difference between the current route and the path */
  without: Route,
  /** A function to navigate to the "to" state */
  open: () => mixed,
  /** A function to navigate to the "without" state */
  close: () => mixed,
|};

const useRouteState = (path: { [string]: any }): RouteState => {
  const { route, navigate } = React.useContext(routerContext);
  const to = {
    ...route,
    ...path,
  };

  const matches = match(
    route,
    Object.entries(path).reduce(
      (accum, [key, value]) =>
        Object.assign(accum, { [key]: routeValue => deepEqual(value, routeValue) }),
      {},
    ),
  );

  const without = { ...route };
  Object.keys(path).forEach(key => {
    delete without[key];
  });

  const open = React.useCallback(() => {
    navigate(to);
  }, [navigate, to]);

  const close = React.useCallback(() => {
    if (window?.history?.state != null) {
      window.history.back();
    } else {
      navigate(without);
    }
  }, [navigate, without]);

  return { matches, to, without, open, close };
};

export default useRouteState;
